import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/error-handler';
import { ActivityWithRelations, ActivityFormData, ActivityType } from '@/types/activity.types';
import { Activity } from '@/components/dashboard/RecentActivity';

// Dashboard activity functions
export const fetchRecentActivitiesForDashboard = async (limit = 5): Promise<Activity[]> => {
  try {
    // First, fetch recent interactions
    const { data: interactions, error: interactionsError } = await supabase
      .from('interactions')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (interactionsError) {
      logError('Error fetching interactions:', interactionsError);
      return [];
    }

    if (!interactions || interactions.length === 0) {
      return [];
    }

    // Get unique entity IDs by type
    const contactIds = interactions
      .filter((i) => i.entity_type === 'contact')
      .map((i) => i.entity_id);

    const orgIds = interactions
      .filter((i) => i.entity_type === 'organization')
      .map((i) => i.entity_id);

    // Fetch contacts if needed
    const contactsMap = new Map();
    if (contactIds.length > 0) {
      const { data: contacts } = await supabase
        .from('contacts')
        .select('id, first_name, last_name')
        .in('id', contactIds);

      if (contacts) {
        contacts.forEach((c) => contactsMap.set(c.id, c));
      }
    }

    // Fetch organizations if needed
    const orgsMap = new Map();
    if (orgIds.length > 0) {
      const { data: orgs } = await supabase
        .from('organizations')
        .select('id, name')
        .in('id', orgIds);

      if (orgs) {
        orgs.forEach((o) => orgsMap.set(o.id, o));
      }
    }

    // Map interactions to activities
    return interactions.map((interaction) => {
      let title = interaction.interaction_type || 'Activity';
      const description = interaction.notes || 'Activity logged';

      if (interaction.entity_type === 'contact' && contactsMap.has(interaction.entity_id)) {
        const contact = contactsMap.get(interaction.entity_id);
        title = `${interaction.interaction_type} with ${contact.first_name} ${contact.last_name}`;
      } else if (interaction.entity_type === 'organization' && orgsMap.has(interaction.entity_id)) {
        const org = orgsMap.get(interaction.entity_id);
        title = `${interaction.interaction_type} with ${org.name}`;
      }

      return {
        id: interaction.id,
        type: interaction.entity_type as any,
        title,
        description,
        time: new Date(interaction.created_at).toLocaleString(),
        user: {
          name: 'Unknown User',
          avatar: undefined,
        },
      };
    });
  } catch (error) {
    logError('Error in fetchRecentActivitiesForDashboard:', error);
    return [];
  }
};

export const fetchUpcomingTasks = async (limit = 3) => {
  const { data, error } = await supabase
    .from('tasks')
    .select(
      `
      *,
      organization:organization_id (name),
      contact:contact_id (first_name, last_name)
    `
    )
    .eq('status', 'pending')
    .order('due_date', { ascending: true })
    .limit(limit);

  if (error) {
    return [];
  }

  if (!data || data.length === 0) {
    return [];
  }

  return data.map((task) => ({
    id: task.id,
    title: task.title,
    dueDate: task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No date set',
    priority: (task.priority || 'medium') as 'low' | 'medium' | 'high',
    status: (task.status || 'pending') as 'pending' | 'in-progress' | 'completed',
    relatedTo: task.organization
      ? { type: 'Organization', name: task.organization.name }
      : task.contact
        ? { type: 'Contact', name: `${task.contact.first_name} ${task.contact.last_name}` }
        : undefined,
  }));
};

// Main activity service functions
export const fetchActivities = async ({ start, end }: { start?: string; end?: string } = {}) => {
  let query = supabase.from('activities').select(`
      *,
      organization:organization_id (id, name),
      contact:contact_id (id, first_name, last_name)
    `);

  // Apply date range filter if provided
  if (start) {
    query = query.gte('date', start);
  }

  if (end) {
    query = query.lte('date', end);
  }

  query = query.order('date', { ascending: false });

  const { data, error } = await query;

  if (error) throw error;
  return data as ActivityWithRelations[];
};

export const fetchActivityById = async (id: string) => {
  const { data, error } = await supabase
    .from('activities')
    .select(
      `
      *,
      organization:organization_id (id, name),
      contact:contact_id (id, first_name, last_name)
    `
    )
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as ActivityWithRelations;
};

export const createActivity = async (activityData: ActivityFormData) => {
  const { data, error } = await supabase.from('activities').insert(activityData).select().single();

  if (error) throw error;
  return data;
};

export const updateActivity = async (id: string, activityData: Partial<ActivityFormData>) => {
  const { data, error } = await supabase
    .from('activities')
    .update(activityData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteActivity = async (id: string) => {
  const { error } = await supabase.from('activities').delete().eq('id', id);

  if (error) throw error;
  return true;
};

// Function to fetch activities for a specific organization
export const fetchActivitiesByOrganization = async (organizationId: string) => {
  const { data, error } = await supabase
    .from('activities')
    .select(
      `
      *,
      organization:organization_id (id, name),
      contact:contact_id (id, first_name, last_name)
    `
    )
    .eq('organization_id', organizationId)
    .order('date', { ascending: false });

  if (error) throw error;
  return data as ActivityWithRelations[];
};

// Function to fetch activities for a specific contact
export const fetchActivitiesByContact = async (contactId: string) => {
  const { data, error } = await supabase
    .from('activities')
    .select(
      `
      *,
      organization:organization_id (id, name),
      contact:contact_id (id, first_name, last_name)
    `
    )
    .eq('contact_id', contactId)
    .order('date', { ascending: false });

  if (error) throw error;
  return data as ActivityWithRelations[];
};

// Function to fetch recent activities
export const fetchRecentActivities = async (limit: number = 5) => {
  const { data, error } = await supabase
    .from('activities')
    .select(
      `
      *,
      organization:organization_id (id, name),
      contact:contact_id (id, first_name, last_name)
    `
    )
    .order('date', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data as ActivityWithRelations[];
};
